import i18n from 'localization/i18nextConf';
import toast from 'react-hot-toast';
import { sanitizeInput } from 'utils/sanitizeInput';
import { secureStorage } from 'utils/secureStorage';

const BASE_URL = import.meta.env.VITE_API_URL;
// Prefer environment override, fallback to the provided microservice host
const MICROSERVICE_URL =
	import.meta.env.VITE_MICROSERVICE_URL || 'https://microservice.stassify.com';
const appBaseUrl = import.meta.env.VITE_APP_BASE_URL;

// Add a flag to track if logout process is already initiated
let isLoggingOut = false;

interface ApiResponse {
	success: boolean;
	data?: any;
	message?: string;
	status?: number;
}

const request = async (url: string, options: any = {}): Promise<ApiResponse> => {
	const storageData = secureStorage.getItem('auth') || {};
	const token = storageData?.state?.userInfo?.token ?? '';

	try {
		const activeLanguage = i18n.language ?? 'en';

		options.headers = {
			...options.headers,
			'Accept-Language': activeLanguage,
			'X-Jobstas-Version': '1.0.0',
			'X-Jobstas-Platform': 'web',
		};

		if (token) {
			options.headers = {
				...options.headers,
				Authorization: `Bearer ${token}`,
			};
		}

		const response = await fetch(url, options);

		// If unauthorized and not already logging out, handle the logout
		if (response.status === 401 && !isLoggingOut) {
			handleLogout(await response.json());
			return {
				success: false,
				status: 401,
			};
		}

		// Handle non-JSON responses
		const contentType = response.headers.get('content-type');
		if (contentType && contentType.includes('application/json')) {
			const jsonResponse = await response.json();
			return {
				...jsonResponse,
				status: response.status,
			};
		}

		return {
			success: false,
			status: response.status,
			message: 'Invalid response format',
		};
	} catch (error: any) {
		return {
			success: false,
			message: `API request failed: ${error.message}`,
		};
	}
};

// Handle logout process
const handleLogout = (response: any) => {
	if (isLoggingOut) return; // Prevent multiple executions

	isLoggingOut = true;
	toast.remove(); // Remove any existing toasts

	const message = response?.message || 'Your session has expired. Please login again.';
	toast.error(message, {
		duration: 3000,
		position: 'top-center',
	});

	// Clear auth data
	secureStorage.removeItem('auth');

	// Redirect after a short delay
	setTimeout(() => {
		window.location.href = `${appBaseUrl}/login`;
		isLoggingOut = false; // Reset the flag after redirect
	}, 2000);
};

// Regular error toast for non-auth errors
const showErrorToast = (message: string) => {
	if (!isLoggingOut) {
		// Only show error toasts if not logging out
		toast.remove();
		toast.error(message, {
			duration: 3000,
			position: 'top-center',
		});
	}
};

export const handleError = async (response: ApiResponse) => {
	const { status, message } = response ?? {};

	if (status === 401) {
		// Skip showing error if already logging out
		return;
	}

	switch (status) {
		case 403:
			showErrorToast(message || 'Access forbidden');
			break;

		default:
			if (message) {
				showErrorToast(message);
			}
	}
};

// Rest of the code (get, post, put, del methods) updated to support hybrid routing
// Define microservice-specific routes so we can route requests to the microservice host when needed.
// This list is easy to extend. Each entry includes a regex to match the path and allowed HTTP methods.
const MICROSERVICE_ROUTES: { regex: RegExp; methods: string[] | '*' }[] = [
	// Chat
	{ regex: /^\/chat$/, methods: ['GET', 'POST'] },
	{ regex: /^\/chat\/messages-by-last-id$/, methods: ['GET'] },
	{ regex: /^\/chat\/details$/, methods: ['GET'] },
	{ regex: /^\/chat\/call-token$/, methods: ['POST'] },
	// DELETE /chat/{roomId}
	{ regex: /^\/chat\/[^\/]+$/, methods: ['DELETE'] },
	// Job
	{ regex: /^\/job$/, methods: ['POST'] },
	{ regex: /^\/job\/list$/, methods: ['GET'] },
	// GET /job/{id} - only route to microservice when the path looks like an ID
	// Match numeric IDs or UUID-like (hex and hyphens) or longer hex strings (e.g. Mongo ObjectId)
	{ regex: /^\/job\/(?:\d+|[0-9a-fA-F-]{8,36})$/, methods: ['GET'] },
	// Social Media
	{ regex: /^\/social-media\/post-list$/, methods: ['GET'] },
	{ regex: /^\/social-media\/static-post-list$/, methods: ['GET'] },
	// Util
	{ regex: /^\/util\/multi-search$/, methods: ['GET'] },
];

/**
 * Determine whether a given relative path + method should use the microservice base URL.
 * - If a full URL is passed (starts with http), we do not rewrite it.
 */
export const shouldUseMicroservice = (url: string, method: string = 'GET') => {
	try {
		// If the url is absolute, keep it as-is (don't route)
		if (/^https?:\/\//i.test(url)) return false;

		// Normalize path (ensure leading slash, strip query string)
		const path = url.split('?')[0].startsWith('/') ? url.split('?')[0] : `/${url.split('?')[0]}`;
		const m = method?.toUpperCase() || 'GET';

		for (const route of MICROSERVICE_ROUTES) {
			const methods = route.methods;
			if (route.regex.test(path) && (methods === '*' || methods.includes(m))) return true;
		}
		return false;
	} catch (e) {
		return false;
	}
};

export const getFullUrl = (url: string, method: string = 'GET') => {
	if (/^https?:\/\//i.test(url)) return url;
	const base = shouldUseMicroservice(url, method) ? MICROSERVICE_URL : BASE_URL;
	// Ensure no double slashes
	return `${base.replace(/\/$/, '')}${url.startsWith('/') ? url : `/${url}`}`;
};

export const get = (url: string, options: any = {}) =>
	request(getFullUrl(url, 'GET'), { ...options, method: 'GET' });

export const post = (
	url: string,
	data?: any,
	options: any = {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
	},
) => {
	const payload = sanitizeInput({ ...data });
	return request(getFullUrl(url, 'POST'), {
		...options,
		method: 'POST',
		body: JSON.stringify(payload),
	});
};

export const put = (
	url: string,
	data?: any,
	options: any = {
		method: 'PUT',
		headers: {
			'Content-Type': 'application/json',
		},
	},
) => {
	const payload = sanitizeInput({ ...data });
	return request(getFullUrl(url, 'PUT'), {
		...options,
		method: 'PUT',
		body: JSON.stringify(payload),
	});
};

export const del = (
	url: string,
	data?: any,
	options: any = {
		method: 'DELETE',
		headers: {
			'Content-Type': 'application/json',
		},
	},
) => {
	const payload = sanitizeInput({ ...data });
	return request(getFullUrl(url, 'DELETE'), {
		...options,
		method: 'DELETE',
		body: JSON.stringify(payload),
	});
};

export const handleResponse = (response: ApiResponse) => {
	if (!response?.success) {
		handleError(response);
		return;
	}
	return response?.data ? response.data : response;
};

export const changeLanguage = (language: string) => {
	i18n
		.changeLanguage(language)
		.then(() => {
			console.log(`Language changed to ${language}`);
		})
		.catch((error) => {
			console.error('Language change failed:', error);
		});
};
